import PaymentStatementReview from '@models/paymentStatementReview';
import RefundLog, { IamportPaymentsCancelData, RefundActor, TossPaymentsCancelData } from '@models/refundLog';
import { DiscountInfo, QuestionOption } from './product';
import { ProgramScheduleUser } from './programSchedule';
import { SalesChannelsType } from './sales-channels';
import { ClassSchedule } from './schedule';
import { UserDiscountCoupon } from './user-discount-coupon';

export enum ReserveStatus {
  RESERVE = 'reserve',
  DONE = 'done',
  CANCELED = 'canceled',
  NONE = 'none',
}

export enum RefundStatus {
  DONE = 'done',
  FAILED = 'failed',
  RESERVE = 'reserve',
  CANCELED = 'canceled',
  REQUESTED = 'requested',
  ACCEPTED = 'accepted',
}
export enum RefundSubmissionStatus {
  NOTIFIED = 'notified',
  NONE = 'none',
}
export enum PaymentStatementRefundStatus {
  NONE = 'none', // migrated
  ALL = 'all',
  PARTIAL = 'partial',
  REQUESTED = 'requested',
  ACCEPTED = 'accepted',
  DENIED = 'denied',
  RESERVED = 'reserved',
  REQUEST_CANCELLED = 'request_cancelled',
}
export enum PaymentStatementReportStatus {
  PUBLISHED = 'published',
  EDITTING = 'editting',
}
export enum PaymentStatementScheduleStatus {
  READY = 'ready',
  CANCEL = 'cancel',
  COMPLETED = 'completed',
}
export const PAYMENT_REFUND_STATUS_LABEL: Record<PaymentStatementRefundStatus | 'NONE' | 'refundRegister', string> = {
  [PaymentStatementRefundStatus.NONE]: '결제 완료',
  [PaymentStatementRefundStatus.ALL]: '전체 환불',
  [PaymentStatementRefundStatus.PARTIAL]: '부분 환불',
  [PaymentStatementRefundStatus.REQUESTED]: '환불 요청',
  [PaymentStatementRefundStatus.RESERVED]: '환불 요청',
  [PaymentStatementRefundStatus.ACCEPTED]: '환불 예정',
  [PaymentStatementRefundStatus.DENIED]: '환불 반려',
  [PaymentStatementRefundStatus.REQUEST_CANCELLED]: '환불 요청 취소',
  NONE: '결제 완료',
  refundRegister: '환불 요청',
};

export interface PaymentStatementUser {
  id?: string;
  name?: string;
  phone?: string;
  contactUri?: string;
}
export interface PaymentStatementProduct {
  saleType?: string;
  id: string;
  name: string;
  classInvoiceId?: string;
  sellerId?: string;
  option?: {
    optionSelectType?: string;
    useRequiredPurchaseQuantityOptions?: boolean;
  };
  isFavorited?: boolean;
  duration?: number;
  durations?: { hour?: number; minute?: number; duration?: number }[];
  questionOptions?: QuestionOption[];
  requestAmount?: number;
  optionName?: string;
  applyGroupDiscount?: boolean; // only class
  applyPeriodDiscount?: boolean; // only commerce
  periodDiscountExcludeChannels?: string[]; // naver, kakao, benepia, app
  discountInfo?: DiscountInfo; // only commerce
  isLastChance?: boolean;
  lastChanceItemId?: string;
  marketSection?: 'last-chance' | 'hot-deal' | 'early-bird';
  marketMeta?: {
    lastChanceItemId?: string;
    sourceProductId?: string;
    sourceOptionId?: string;
    groupId?: string;
    scheduleDate?: string;
  };
}
export interface PaymentStatementReport {
  id?: string;
  publishedAt?: Date;
  createdAt?: Date;
}
export interface PaymentStatementPurchase {
  option?: string[];
  name: string | string[];
  fee: number;
  count: number;
  uid?: string;
  date?: Date | string;
  time?: string;
  yyyymmdd?: string;
  required?: boolean;
}
export interface PaymentStatementRefundItem extends PaymentStatementPurchase {
  refundedAt?: Date;
}
export interface PaymentStatementPayment {
  method: string;
  gateway: string;
  amount: number;
  billingAmount: number;
  point: number;
  membershipDiscountAmount?: number;
  paidAt?: Date;
  result: { [key: string]: any };
  error?: unknown;
  couponDiscountAmount?: number;
  usedCoupons?: UserDiscountCoupon[];
}
export interface PaymentStatementRefund {
  amount?: number;
  point?: number;
  billingAmount?: number;
  data: (RefundData | RefundLog)[];
  legacyHistory?: RefundData[];
  noMoreRefundsAvailable?: boolean;
  submissionStatus?: RefundSubmissionStatus;
  requestUser?: {
    id: string;
    name?: string;
    nickname?: string;
    requestedBy: RefundActor;
  };
  purchases?: PaymentStatementPurchase[]; // deprecated
  refundItems?: PaymentStatementPurchase[];
  remainingPurchases?: PaymentStatementPurchase[];
  requestedAmount?: number;
  acceptedAmount?: number;
  refundedAmount?: number;
  membershipDiscountAmount?: number;
  requestedAt?: Date;
  canceledAt?: Date;
  acceptedAt?: Date;
  deniedAt?: Date;
  refundedAt?: Date;
}
export interface RefundData {
  // migrated
  _id?: string;
  receiptUrl?: string;
  createdAt?: Date;
  partnerRefundAmout?: number;
  amount?: number;
  billingAmount?: number;
  point?: number;
  refundItems?: PaymentStatementPurchase[];
  status?: RefundStatus;
  submissionStatus?: RefundSubmissionStatus;
  data?: TossPaymentsCancelData | IamportPaymentsCancelData;
}
export interface PaymentStatementShipping {
  name: string;
  phone: string;
  fee: number;
  address: string;
  addressDetail: string;
  invoiceNumber: string;
  company: string;
}
export interface PaymentStatementSettlement {
  ratio: number;
  amount: number;
  transferAmount: number;
  transferAt?: Date;
  estimatedAmount?: number;
  excluded?: boolean;
  excludedAmount?: number;
}
export interface PaymentStatementStatus {
  payment?: PaymentStatementPaymentStatus;
  refund: 'NONE' | 'refundRegister' | PaymentStatementRefundStatus;
  settlement?: 'DONE' | 'NONE' | 'READY' | 'done' | 'none' | 'ready';
  shipping?: string;
  report?: PaymentStatementReportStatus;
}
export type PaymentStatementPaymentStatus =
  | 'DONE' // migrated
  | 'READY' // migrated
  | 'NONE' // migrated
  | 'CANCEL' // migrated
  | 'ERROR' // migrated
  | 'done'
  | 'ready'
  | 'cancel'
  | 'error'
  | 'waiting_for_deposit';
export class PaymentStatementMemo {
  payment?: string;
  paymentPartner?: string;
  refund?: string;
  refundUser?: string;
  refundPartner?: string;
  settlement?: string;
  settlementPartner?: string;
  shipping?: string;
  qna?: string;
  note?: string;
}

export interface PaymentStatementScheduleInfo {
  name?: string;
  birthYear?: string;
  friends?: string;
  ageByYear?: string;
  schoolYear?: string;
  [key: string]: any;
}

export interface PaymentStatementSchedule {
  id: string;
  scheduleName?: string;
  startDate?: Date;
  endDate?: Date;
  localDateString?: string;
  week?: number;
  duration: number;
  scheduleInfo: PaymentStatementScheduleInfo;
  notes?: string;
  status?: PaymentStatementScheduleStatus;
  optionName: string;
  instructor?: ProgramScheduleUser;
}

export interface PaymentStatement {
  _id: string;
  uuid: string;
  uid?: string;
  buyer: PaymentStatementUser;
  seller: PaymentStatementUser;
  product: PaymentStatementProduct;
  report?: PaymentStatementReport;
  purchases: PaymentStatementPurchase[];
  refunds?: PaymentStatementRefundItem[];
  payment: PaymentStatementPayment;
  refund?: PaymentStatementRefund;
  shipping?: PaymentStatementShipping;
  settlement?: PaymentStatementSettlement;
  statuses: PaymentStatementStatus;
  memo: PaymentStatementMemo;
  schedules: PaymentStatementSchedule[];
  legacy?: object;
  createdAt: Date;
  updatedAt: Date;
  expiredAt?: Date;
  review?: PaymentStatementReview[];
  plannedDates: Date[];
  firstPlannedDates: Date;
  completionDate?: Date;
}

export type PaymentStatusType = 'payment' | 'ready' | Omit<PaymentStatementRefundStatus, 'none' | 'reserved'>;
export type SortType = 'payment.paidAt' | 'plannedDates';
export type SaleType = 'commerce' | 'class' | 'special' | '';

export interface GetPaymentStatementsQuery {
  page: number;
  size: number;
  keyword?: string;
  startDate?: Date | '';
  endDate?: Date | '';
  paymentStatus?: PaymentStatusType[];
  sortType?: SortType;
  saleType?: SaleType;
  saleTypeCategories?: Exclude<SaleType, ''>[];
  settlementStatus?: string;
  excludeAmount?: boolean;
  channel?: SalesChannelsType[];
  noneScheduledOrder?: boolean;
}
export interface RefundBody {
  type: 'accept' | 'deny' | 'request';
  content?: string;
  fee?: number;
  noMoreRefundsAvailable?: boolean;
  refundItems?: PaymentStatementPurchase[];
  channel?: SalesChannelsType;
}

export interface GetPurchasesQuery {
  startDate?: Date | '';
  endDate?: Date | '';
  keyword?: string;
  searchText?: string;
  paymentStatus?: PaymentStatusType[];
  sortType?: string;
  page?: number;
  size?: number;
  withoutAmount?: boolean;
  channel?: string;
}

export type ClassChangePurchaseItem = ScheduleInfo<Pick<ClassSchedule, '_id' | 'startDate'>>;
export type CommerceChangePurchasesItem = ScheduleInfo<PaymentStatementPurchase>;
interface ScheduleInfo<T> {
  currentItem: T;
  newItem: string;
}
export interface ChangeClassScheduleBody {
  saleType: 'class';
  changePurchaseItems: ClassChangePurchaseItem[];
}
export interface ChangeCommerceOptionBody {
  saleType: 'commerce';
  changePurchaseItems: CommerceChangePurchasesItem[];
  paidAt: Date;
  productId: string;
}

export default PaymentStatement;
